"use client";

import { createContext, useState, useEffect, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import type { AuthState, User } from "@/types";

export const AuthContext = createContext<AuthState>({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
});

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Decode JWT payload without verification (client-side — API validates signature).
 * Returns null if the token is missing or malformed.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = atob(padded);
    return JSON.parse(payload) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function AuthProvider({
  children,
}: AuthProviderProps): React.JSX.Element {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });
  // Re-read the cookie whenever the route changes. The login Server Action
  // sets `access_token` and triggers a soft (client-side) redirect to
  // /dashboard — without this dependency, AuthProvider stays mounted across
  // the redirect and never picks up the new cookie, so the (auth) layout
  // bounces the user straight back to /login. See actions.ts.
  const pathname = usePathname();

  useEffect(() => {
    // Read cookie on mount and on every route change.
    const token = getCookie("access_token");
    if (!token) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- bootstrapping from external state (cookie); no updates after mount
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }

    const claims = decodeJwtPayload(token);
    if (!claims) {
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }

    const user: User = {
      email: (claims["email"] as string) ?? "",
      tenantId: (claims["custom:tenant_id"] as string) ?? "",
      tenantName: (claims["custom:tenant_name"] as string) ?? "",
      role: ((claims["custom:role"] as string) ?? "viewer") as User["role"],
    };

    setAuthState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
  }, [pathname]);

  return <AuthContext value={authState}>{children}</AuthContext>;
}
